import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Plus,
  Calendar,
  Loader2,
  ClipboardList,
  CheckCircle2,
  Clock,
  Play,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  planned: 'bg-slate-100 text-slate-700',
  active: 'bg-green-100 text-green-700',
  completed: 'bg-blue-100 text-blue-700',
};

const statusNames = {
  planned: 'Planlandı',
  active: 'Aktif',
  completed: 'Tamamlandı',
};

const periodTypeNames = {
  guz: 'Güz',
  bahar: 'Bahar',
  yaz: 'Yaz',
};

export const ScreeningsPage = () => {
  const [periods, setPeriods] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [newPeriod, setNewPeriod] = useState({
    name: '',
    academic_year: '2025-2026',
    period_type: 'guz',
    start_date: '',
    end_date: '',
    assigned_class_ids: [],
  });

  const fetchPeriods = async () => {
    try {
      const response = await fetch(`${API_URL}/api/screening-periods`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setPeriods(data);
      }
    } catch (error) {
      console.error('Failed to fetch periods:', error);
      toast.error('Tarama dönemleri yüklenirken hata oluştu');
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await fetch(`${API_URL}/api/classes`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setClasses(data);
      }
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchPeriods(), fetchClasses()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const handleAddPeriod = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/screening-periods`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newPeriod),
      });
      
      if (response.ok) {
        toast.success('Tarama dönemi oluşturuldu');
        setIsAddDialogOpen(false);
        setNewPeriod({
          name: '',
          academic_year: '2025-2026',
          period_type: 'guz',
          start_date: '',
          end_date: '',
          assigned_class_ids: [],
        });
        fetchPeriods();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Dönem oluşturulamadı');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setAddLoading(false);
    }
  };

  const handleStatusChange = async (periodId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/screening-periods/${periodId}/status?status=${newStatus}`, {
        method: 'PUT',
        credentials: 'include',
      });
      
      if (response.ok) {
        toast.success('Dönem durumu güncellendi');
        fetchPeriods();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Güncelleme başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const toggleClassSelection = (classId) => {
    setNewPeriod(prev => ({
      ...prev,
      assigned_class_ids: prev.assigned_class_ids.includes(classId)
        ? prev.assigned_class_ids.filter(id => id !== classId)
        : [...prev.assigned_class_ids, classId]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="screenings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-primary-900">
            Tarama Dönemleri
          </h1>
          <p className="text-muted-foreground">
            Öğrenci değerlendirme dönemlerini yönetin
          </p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-period-btn">
              <Plus className="mr-2 h-4 w-4" />
              Yeni Dönem
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Yeni Tarama Dönemi</DialogTitle>
              <DialogDescription>
                Tarama dönemi bilgilerini girin
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddPeriod}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Dönem Adı</Label>
                  <Input
                    id="name"
                    placeholder="örn: 2025-2026 Güz Taraması"
                    value={newPeriod.name}
                    onChange={(e) => setNewPeriod({...newPeriod, name: e.target.value})}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Öğretim Yılı</Label>
                    <Select
                      value={newPeriod.academic_year}
                      onValueChange={(value) => setNewPeriod({...newPeriod, academic_year: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2024-2025">2024-2025</SelectItem>
                        <SelectItem value="2025-2026">2025-2026</SelectItem>
                        <SelectItem value="2026-2027">2026-2027</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Dönem Türü</Label>
                    <Select
                      value={newPeriod.period_type}
                      onValueChange={(value) => setNewPeriod({...newPeriod, period_type: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="guz">Güz</SelectItem>
                        <SelectItem value="bahar">Bahar</SelectItem>
                        <SelectItem value="yaz">Yaz</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Başlangıç Tarihi</Label>
                    <Input
                      type="date"
                      value={newPeriod.start_date}
                      onChange={(e) => setNewPeriod({...newPeriod, start_date: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Bitiş Tarihi</Label>
                    <Input
                      type="date"
                      value={newPeriod.end_date}
                      onChange={(e) => setNewPeriod({...newPeriod, end_date: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Dahil Edilecek Sınıflar</Label>
                  <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                    {classes.map(cls => (
                      <div key={cls.class_id} className="flex items-center space-x-2">
                        <Checkbox
                          id={cls.class_id}
                          checked={newPeriod.assigned_class_ids.includes(cls.class_id)}
                          onCheckedChange={() => toggleClassSelection(cls.class_id)}
                        />
                        <label htmlFor={cls.class_id} className="text-sm cursor-pointer">
                          {cls.name} ({cls.student_count} öğrenci)
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                  İptal
                </Button>
                <Button type="submit" disabled={addLoading || !newPeriod.name || newPeriod.assigned_class_ids.length === 0}>
                  {addLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Oluştur
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Periods List */}
      <div className="space-y-4">
        {periods.map((period) => (
          <Card key={period.period_id} className="hover:shadow-md transition-shadow">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold">{period.name}</h3>
                    <Badge variant="outline" className={statusColors[period.status]}>
                      {period.status === 'active' && <Play className="mr-1 h-3 w-3" />}
                      {period.status === 'completed' && <CheckCircle2 className="mr-1 h-3 w-3" />}
                      {period.status === 'planned' && <Clock className="mr-1 h-3 w-3" />}
                      {statusNames[period.status]}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {period.start_date} - {period.end_date}
                    </span>
                    <span>{period.academic_year} {periodTypeNames[period.period_type]}</span>
                    <span>{period.assigned_class_ids?.length || 0} sınıf</span>
                  </div>
                </div>
                
                <div className="flex flex-col items-end gap-2">
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary-900">
                      {period.completed_students}/{period.total_students}
                    </p>
                    <p className="text-sm text-muted-foreground">öğrenci tamamlandı</p>
                  </div>
                  <div className="w-48">
                    <Progress value={period.completion_rate} className="h-2" />
                    <p className="text-xs text-muted-foreground text-right mt-1">
                      %{period.completion_rate} tamamlandı
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex gap-2 mt-4 pt-4 border-t">
                {period.status === 'planned' && (
                  <Button 
                    size="sm" 
                    onClick={() => handleStatusChange(period.period_id, 'active')}
                  >
                    <Play className="mr-2 h-4 w-4" />
                    Başlat
                  </Button>
                )}
                {period.status === 'active' && (
                  <Button 
                    size="sm"
                    variant="secondary"
                    onClick={() => handleStatusChange(period.period_id, 'completed')}
                  >
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Tamamla
                  </Button>
                )}
                <Button variant="outline" size="sm">
                  <ClipboardList className="mr-2 h-4 w-4" />
                  Detaylar
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        
        {periods.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              Henüz tarama dönemi oluşturulmamış
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ScreeningsPage;
